import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useFavoriteSessions from '../data/exercise-sessions/favorite/useFavoriteSessions';
import { useExerciseTypes } from '../data/exercises/useExerciseTypes';
import useHandleClickOutside from '../utils/useHandleClickOutside';
import { getNowHHmm } from '../../helpers';

const STEP_SEARCH_ALIASES = ['steg', 'steps', 'stegräknare'];

// HELPERS

const splitExerciseName = (name) => {
    const [base, suffix = ''] = name.toLowerCase().split(' - ');
    const numeric = suffix.match(/\d+(\.\d+)?/)?.[0];

    return {
        base,
        suffix,
        numericValue: numeric ? Number(numeric) : null
    };
};

const compareExerciseNames = (a, b) => {
    const aParts = splitExerciseName(a);
    const bParts = splitExerciseName(b);

    // BASE NAME
    if (aParts.base !== bParts.base) {
        return aParts.base.localeCompare(bParts.base, 'sv');
    }

    // NUMERIC SUFFIX (e.g. 5 km vs 10 km)
    if (aParts.numericValue != null && bParts.numericValue != null) {
        return aParts.numericValue - bParts.numericValue;
    }

    // FALLBACK STRING COMPARISON
    return aParts.suffix.localeCompare(bParts.suffix, 'sv', { numeric: true });
};

const useExerciseForm = () => {
    const [form, setForm] = useState({
        exercise_type: '',
        exercise_id: '',
        exercise_name: '',
        exercise_steps: '',
        exercise_duration: '45',
        exercise_time: getNowHHmm(),
        exercise_comment: ''
    });

    // SEARCH + SELECTION STATE
    const [selectedResult, setSelectedResult] = useState(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showInvalidMessage, setShowInvalidMessage] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    const dropdownRef = useRef(null);
    const timeoutRef = useRef(null);

    // DATA SOURCES
    const { data: exerciseTypesData, isLoading, isError } = useExerciseTypes();
    const { data: favoriteSessions = [] } = useFavoriteSessions();

    const exerciseTypes = useMemo(() => exerciseTypesData?.types ?? [], [exerciseTypesData]);

    // FILTERED SEARCH RESULTS (POLYMORPHIC)
    const filteredResults = useMemo(() => {
        if (!form.exercise_name?.trim()) return [];

        const q = form.exercise_name.toLowerCase();

        const typeResults = exerciseTypes
            .filter((t) => t.name.toLowerCase().includes(q))
            .sort((a, b) => compareExerciseNames(a.name, b.name))
            .map((t) => ({
                kind: 'exercise_type',
                data: t
            }));

        const favoriteResults = favoriteSessions
            .filter((f) => f.name.toLowerCase().includes(q))
            .sort((a, b) => compareExerciseNames(a.name, b.name))
            .map((f) => ({
                kind: 'favorite_session',
                data: f
            }));

        const stepResult = STEP_SEARCH_ALIASES.some((alias) => alias.includes(q))
            ? [{ kind: 'steps', data: { id: 'steps', name: 'Stegräknare' } }]
            : [];

        return [...stepResult, ...favoriteResults, ...typeResults];
    }, [form.exercise_name, exerciseTypes, favoriteSessions]);

    // INVALID MESSAGE HANDLING
    useEffect(() => {
        clearTimeout(timeoutRef.current);

        if (!hasUserInteracted || !form.exercise_name || selectedResult) {
            setShowInvalidMessage(false);
            return;
        }

        timeoutRef.current = setTimeout(() => {
            setShowInvalidMessage(true);
        }, 2000);

        return () => clearTimeout(timeoutRef.current);
    }, [form.exercise_name, selectedResult, hasUserInteracted]);

    useHandleClickOutside([dropdownRef], showSuggestions, setShowSuggestions);

    // HANDLERS
    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value
        }));

        if (name === 'exercise_name') {
            setHasUserInteracted(true);
            setSelectedResult(null);
            setShowSuggestions(true);
            setShowInvalidMessage(false);
        }
    };

    const handleSelect = (result) => {
        setSelectedResult(result);

        if (result.kind === 'exercise_type') {
            setForm((prev) => ({
                ...prev,
                exercise_type: 1,
                exercise_id: result.data.id,
                exercise_name: result.data.name
            }));
        }

        if (result.kind === 'steps') {
            setForm((prev) => ({
                ...prev,
                exercise_type: 2,
                exercise_id: '',
                exercise_name: 'Stegräknare',
                exercise_duration: '',
                exercise_steps: ''
            }));
        }

        setShowSuggestions(false);
        setShowInvalidMessage(false);

        return result;
    };

    const clearSearch = () => {
        setForm((prev) => ({
            ...prev,
            exercise_type: '',
            exercise_id: '',
            exercise_name: ''
        }));

        setSelectedResult(null);
        setShowSuggestions(false);
        setShowInvalidMessage(false);
    };

    const resetForm = useCallback((values = {}) => {
        setForm((prev) => ({
            ...prev,
            ...values
        }));
        setSelectedResult(null);
    }, []);

    // PUBLIC API
    return {
        form,
        setForm,

        selectedResult,
        filteredResults,
        exerciseTypes,
        showSuggestions,
        showInvalidMessage,
        dropdownRef,

        handleChange,
        handleSelect,
        clearSearch,
        resetForm,

        isLoading,
        isError
    };
};

export default useExerciseForm;
